import { defineStore } from 'pinia';

const USER_STORAGE_KEY = 'user';
const BEARER_STORAGE_KEY = 'bearer';

const canUseStorage = () => typeof window !== 'undefined' && !!window.localStorage;

const parseStoredUser = () => {
    if (!canUseStorage()) {
        return null;
    }

    const rawUser = window.localStorage.getItem(USER_STORAGE_KEY);
    if (!rawUser) {
        return null;
    }

    try {
        return JSON.parse(rawUser);
    } catch (_err) {
        return null;
    }
};

const normalizeBearer = (value = '') => {
    const rawValue = `${value || ''}`.trim();
    if (!rawValue) {
        return '';
    }

    return rawValue.toLowerCase().startsWith('bearer ') ? rawValue : `Bearer ${rawValue}`;
};

const readStoredBearer = () => {
    if (!canUseStorage()) {
        return '';
    }
    return normalizeBearer(window.localStorage.getItem(BEARER_STORAGE_KEY) || '');
};

const persistSession = ({ user = null, bearer = '' }) => {
    if (!canUseStorage()) {
        return;
    }

    if (user) {
        window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
        window.localStorage.removeItem(USER_STORAGE_KEY);
    }

    if (bearer) {
        window.localStorage.setItem(BEARER_STORAGE_KEY, bearer);
    } else {
        window.localStorage.removeItem(BEARER_STORAGE_KEY);
    }
};

export const useUser = defineStore('user', {
    state: () => {
        const user = parseStoredUser();
        const bearer = readStoredBearer();

        return {
            user,
            bearer,
            isLoggedIn: !!(user && bearer),
        };
    },
    actions: {
        setSession(data = {}) {
            const nextUser = typeof data.user === 'string'
                ? (() => {
                    try {
                        return JSON.parse(data.user);
                    } catch (_err) {
                        return null;
                    }
                })()
                : (data.user || null);
            const nextBearer = normalizeBearer(data.token || data.bearer || '');

            this.user = nextUser;
            this.bearer = nextBearer;
            this.isLoggedIn = !!(nextUser && nextBearer);
            persistSession({ user: nextUser, bearer: nextBearer });
        },
        hydrateFromStorage() {
            const nextUser = parseStoredUser();
            const nextBearer = readStoredBearer();

            this.user = nextUser;
            this.bearer = nextBearer;
            this.isLoggedIn = !!(nextUser && nextBearer);
        },
        clearSession() {
            this.user = null;
            this.bearer = '';
            this.isLoggedIn = false;
            persistSession({ user: null, bearer: '' });
        },
        patchUser(data = {}) {
            if (!this.user || typeof this.user !== 'object') {
                return;
            }

            this.user = {
                ...this.user,
                ...data
            };
            persistSession({ user: this.user, bearer: this.bearer });
        },
        login(_loginType, data = {}) {
            this.setSession(data);
        },
        logout() {
            this.clearSession();
        }
    },
    getters: {
        isAuthenticated: (state) => !!(state.isLoggedIn && state.user && state.bearer),
        currentUser: (state) => state.user,
        accessToken: (state) => state.bearer
    },
});
